// menuAdmin.js — construye el árbol del menú lateral a partir del catálogo
// estático (menuItems, en constants.js) aplicando las sobrescrituras que
// guarda el admin desde el Editor de menú (netlify/functions/
// updateMenuAdmin.js, documento configuracion/menuAdmin):
//   - visibilidadPorRol: qué roles ven cada id (sustituye a item.roles)
//   - parentOverride: bajo qué id aparece anidado cada id (null = suelto)
//   - orden: orden global preferido de ids (top-level y anidados a la vez;
//     el orden entre hermanos es lo único que importa al renderizar)
//   - gruposVirtuales: { [id]: { label, icon } } — contenedores creados por
//     el admin que no existen en el catálogo estático, se comportan como
//     cualquier item de nivel superior con subitems
// El resultado ya viene filtrado por rol, ordenado y con los subitems
// recalculados, en el mismo formato que menuItems — el resto del menú no
// necesita saber que hay overrides.

function puedeVer(roles, role) {
  return !roles || roles.includes(role);
}

// id -> { item, parentOriginal } — aplana top-level + subitems + grupos
// virtuales (sin padre propio, se comportan como top-level) en un solo mapa
function aplanar(menuItems, gruposVirtuales) {
  const mapa = new Map();
  for (const item of menuItems || []) {
    mapa.set(item.id, { item, parentOriginal: null });
    for (const sub of item.subitems || []) {
      mapa.set(sub.id, { item: sub, parentOriginal: item.id });
    }
  }
  for (const [id, grupo] of Object.entries(gruposVirtuales || {})) {
    mapa.set(id, { item: { id, label: grupo.label, icon: grupo.icon, description: grupo.label }, parentOriginal: null, esVirtual: true });
  }
  return mapa;
}

// Ordena `ids` según su posición en `orden` (los que no aparecen van al
// final, conservando su orden relativo original)
function ordenarPorPreferencia(ids, orden) {
  const posDe = new Map((orden || []).map((id, i) => [id, i]));
  return ids
    .map((id, i) => ({ id, i }))
    .sort((a, b) => {
      const pa = posDe.has(a.id) ? posDe.get(a.id) : orden.length + a.i;
      const pb = posDe.has(b.id) ? posDe.get(b.id) : orden.length + b.i;
      return pa - pb;
    })
    .map(x => x.id);
}

export function construirArbolMenu(menuItems, overrides, role) {
  const visibilidadPorRol = overrides?.visibilidadPorRol || {};
  const parentOverride    = overrides?.parentOverride    || {};
  const orden              = overrides?.orden             || [];
  const gruposVirtuales    = overrides?.gruposVirtuales   || {};
  const mapa = aplanar(menuItems, gruposVirtuales);

  // 1. Quién es visible para este rol, usando el rol efectivo (override si existe)
  const visibles = new Set();
  for (const [id, { item }] of mapa) {
    const rolesEfectivos = Object.prototype.hasOwnProperty.call(visibilidadPorRol, id)
      ? visibilidadPorRol[id] : item.roles;
    if (puedeVer(rolesEfectivos, role)) visibles.add(id);
  }

  // 2. Padre efectivo de cada id visible — si el padre efectivo no es visible
  // (o ha dejado de existir, p.ej. un grupo virtual borrado), se promociona
  // a nivel superior en vez de perderse.
  const padreDe = new Map();
  for (const id of visibles) {
    const { parentOriginal } = mapa.get(id);
    let padre = Object.prototype.hasOwnProperty.call(parentOverride, id) ? parentOverride[id] : parentOriginal;
    if (padre && !visibles.has(padre)) padre = null;
    padreDe.set(id, padre || null);
  }

  // 3. Agrupar hijos por padre, ordenados según la preferencia guardada
  const hijosDe = new Map();
  for (const id of visibles) {
    const padre = padreDe.get(id);
    if (!padre) continue;
    if (!hijosDe.has(padre)) hijosDe.set(padre, []);
    hijosDe.get(padre).push(id);
  }
  for (const [padre, hijos] of hijosDe) hijosDe.set(padre, ordenarPorPreferencia(hijos, orden));

  // 4. Reconstruir la lista de nivel superior (ordenada) con sus subitems
  // ya resueltos. Un grupo virtual sin ningún hijo visible no se muestra —
  // es un contenedor puro, sin vista propia a la que navegar.
  const idsTopLevel = ordenarPorPreferencia(
    [...visibles].filter(id => padreDe.get(id) === null), orden
  );
  const topLevel = [];
  for (const id of idsTopLevel) {
    const { item, esVirtual } = mapa.get(id);
    const hijos = (hijosDe.get(id) || []).map(hid => mapa.get(hid).item);
    if (esVirtual && hijos.length === 0) continue;
    topLevel.push({ ...item, subitems: hijos.length > 0 ? hijos : undefined });
  }

  return topLevel;
}
